import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen,
  Award,
  Clock,
  TrendingUp,
  ArrowRight,
  Play,
  Target,
  Heart,
  Flame,
  Zap,
  Trophy,
  Star,
  Rocket,
  GraduationCap,
  Shield,
  CheckCircle2,
  Sunrise,
  BadgeCheck,
  Home,
} from 'lucide-react';
import LearningStyleQuiz from '@/components/LearningStyleQuiz';

const BADGE_ICONS = {
  'rocket': Rocket, 'book-open': BookOpen, 'graduation-cap': GraduationCap,
  'target': Target, 'check-circle': CheckCircle2, 'flame': Flame,
  'zap': Zap, 'trophy': Trophy, 'award': Award, 'badge': BadgeCheck,
  'shield': Shield, 'sunrise': Sunrise,
};

const LearnerDashboard = () => {
  const { user, api } = useAuth();
  const [showQuiz, setShowQuiz] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [cpd, setCpd] = useState(null);
  const [wishlistCourses, setWishlistCourses] = useState([]);
  const [gamification, setGamification] = useState(null);
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCourses: 0,
    completedCourses: 0,
    totalCertificates: 0,
    totalHours: 0,
  });

  const isResident = user?.is_resident;

  useEffect(() => {
    fetchData();
    // Show learning style quiz on first login if not completed
    if (user && !user.learning_quiz_completed) {
      setShowQuiz(true);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [enrollmentsRes, certificatesRes, myCoursesRes, transcriptRes, wishlistRes, gamRes] = await Promise.all([
        api.get('/enrollments').catch(() => ({ data: [] })),
        api.get('/certificates').catch(() => ({ data: [] })),
        api.get('/player/my-courses').catch(() => ({ data: [] })),
        api.get('/compliance/transcript').catch(() => ({ data: null })),
        api.get('/wishlist/courses').catch(() => ({ data: [] })),
        api.get('/gamification/profile').catch(() => ({ data: null })),
      ]);

      setEnrollments(enrollmentsRes.data || []);
      setCertificates(certificatesRes.data || []);
      setMyCourses(myCoursesRes.data || []);
      setWishlistCourses(wishlistRes.data || []);
      setGamification(gamRes.data);
      if (transcriptRes.data?.cpd) setCpd(transcriptRes.data.cpd);

      const enrolled = myCoursesRes.data || enrollmentsRes.data || [];
      const completed = enrolled.filter(e => e.progress?.status === 'completed' || e.status === 'completed').length;
      const cpdHours = transcriptRes.data?.total_learning_hours || 0;
      
      setStats({
        totalCourses: enrolled.length,
        completedCourses: completed,
        totalCertificates: (certificatesRes.data || []).length,
        totalHours: cpdHours,
      });

      // Fetch recommended courses for residents
      if (user?.is_resident) {
        try {
          const recRes = await api.get('/courses/public?course_type=resident&limit=6');
          const enrolledIds = new Set(enrolled.map(e => e.course_id || e.id));
          setRecommendedCourses((recRes.data || []).filter(c => !enrolledIds.has(c.id)));
        } catch { /* silent */ }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Enrolled Courses',
      value: stats.totalCourses,
      icon: BookOpen,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Completed',
      value: stats.completedCourses,
      icon: TrendingUp,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Certificates',
      value: stats.totalCertificates,
      icon: Award,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      title: 'Learning Hours',
      value: `${stats.totalHours}h`,
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
    {
      title: 'Day Streak',
      value: gamification?.current_streak || 0,
      sub: gamification?.longest_streak ? `Best: ${gamification.longest_streak} days` : 'Learn today to start one',
      icon: Flame,
      color: 'text-orange-500',
      bgColor: 'bg-orange-50',
      testId: 'dashboard-streak-card',
    },
  ];

  return (
    <>
    <DashboardLayout requiredRoles={['learner']}>
      <div className="space-y-8" data-testid="learner-dashboard">
        {/* Welcome */}
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">
            Welcome back, {user?.first_name}!
          </h1>
          <p className="text-slate-600 mt-1">
            {isResident ? 'Explore free courses designed to build your independence and skills' : 'Continue your learning journey'}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="border-slate-200" data-testid={stat.testId}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">{stat.title}</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">
                        {stat.value}
                      </p>
                      {stat.sub && <p className="text-xs text-slate-500 mt-0.5">{stat.sub}</p>}
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Recommended Resident Courses */}
        {isResident && recommendedCourses.length > 0 && (
          <div data-testid="recommended-resident-courses">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#2a3e5d] flex items-center gap-2">
                <Home className="h-5 w-5 text-[#10B981]" />
                Recommended for You
              </h2>
              <Link to="/courses?type=resident" className="text-sm text-[#10B981] hover:text-[#059669] font-medium flex items-center gap-1">
                View all <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {recommendedCourses.slice(0, 3).map(course => (
                <Link key={course.id} to={`/courses/${course.id}`}>
                  <Card className="border-slate-200 hover:shadow-lg hover:border-[#10B981]/30 transition-all overflow-hidden group">
                    {course.thumbnail_url && (
                      <div className="h-36 overflow-hidden">
                        <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    )}
                    <CardContent className="p-4">
                      <Badge className="bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20 mb-2 text-xs">Free</Badge>
                      <h3 className="font-semibold text-[#2a3e5d] text-sm line-clamp-2">{course.title}</h3>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{course.short_description}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Gamification: Level, Streak, Points, Badges */}
        {gamification && (
          <div className="space-y-4" data-testid="gamification-section">
            {/* Level + Points + Streak Bar */}
            <Card className="border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#2a3e5d] to-[#20acf6] p-5 text-white">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center backdrop-blur-sm">
                      <span className="text-2xl font-bold">{gamification.level?.level || 1}</span>
                    </div>
                    <div>
                      <p className="text-white/70 text-xs uppercase tracking-wider">Level {gamification.level?.level}</p>
                      <p className="text-lg font-bold">{gamification.level?.name || 'Newcomer'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-5">
                    <div className="text-center">
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 text-amber-300" />
                        <span className="text-xl font-bold">{gamification.total_points}</span>
                      </div>
                      <p className="text-white/60 text-[10px] uppercase">Points</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center gap-1">
                        <Flame className={`h-4 w-4 ${gamification.current_streak > 0 ? 'text-orange-300' : 'text-white/30'}`} />
                        <span className="text-xl font-bold">{gamification.current_streak}</span>
                      </div>
                      <p className="text-white/60 text-[10px] uppercase">Day Streak</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center gap-1">
                        <Trophy className="h-4 w-4 text-amber-300" />
                        <span className="text-xl font-bold">{gamification.earned_count}</span>
                      </div>
                      <p className="text-white/60 text-[10px] uppercase">Badges</p>
                    </div>
                  </div>
                </div>
                {/* Level progress bar */}
                {gamification.level?.next_at && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-white/60 mb-1">
                      <span>Level {gamification.level.level}</span>
                      <span>{gamification.total_points}/{gamification.level.next_at} pts</span>
                    </div>
                    <div className="w-full h-2 bg-white/15 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-white/60 rounded-full transition-all"
                        style={{ width: `${Math.min(100, (gamification.total_points / gamification.level.next_at) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Badges Gallery */}
            <Card className="border-slate-200" data-testid="badges-gallery">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Award className="h-5 w-5 text-amber-500" /> Your Badges
                  <span className="text-sm font-normal text-slate-400 ml-auto">{gamification.earned_count}/{gamification.total_badges}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                  {gamification.badges?.map((badge) => {
                    const Icon = BADGE_ICONS[badge.icon] || Award;
                    return (
                      <div
                        key={badge.id}
                        className={`relative text-center p-3 rounded-xl border-2 transition-all ${
                          badge.earned
                            ? 'border-amber-200 bg-amber-50'
                            : 'border-slate-100 bg-slate-50 opacity-40'
                        }`}
                        data-testid={`badge-${badge.id}`}
                        title={badge.description}
                      >
                        <div className={`w-10 h-10 mx-auto rounded-xl flex items-center justify-center mb-1.5 ${
                          badge.earned ? 'bg-amber-100' : 'bg-slate-100'
                        }`}>
                          <Icon className={`h-5 w-5 ${badge.earned ? 'text-amber-600' : 'text-slate-300'}`} />
                        </div>
                        <p className={`text-[11px] font-semibold leading-tight ${badge.earned ? 'text-[#2a3e5d]' : 'text-slate-400'}`}>
                          {badge.name}
                        </p>
                        {badge.earned && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center">
                            <CheckCircle2 className="h-3 w-3 text-white" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* CPD Hours Widget */}
        {cpd && cpd.total_hours > 0 && (
          <Card className="border-slate-200" data-testid="cpd-widget">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-[#02d1d4]" />
                  <h3 className="font-semibold text-[#2a3e5d] text-sm">CPD Progress ({new Date().getFullYear()})</h3>
                </div>
                <Link to="/transcript" className="text-xs text-[#20acf6] hover:underline flex items-center gap-1">
                  View Transcript <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              <div className="flex items-center gap-4 mb-2">
                <span className="text-2xl font-bold text-[#2a3e5d]">{cpd.hours_this_year}h</span>
                <span className="text-sm text-slate-500">/ 20h annual target</span>
                {cpd.hours_this_year >= 20 && (
                  <span className="text-xs bg-green-100 text-green-700 rounded-full px-2 py-0.5 font-medium">Target Met</span>
                )}
              </div>
              <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden mb-3">
                <div
                  className={`h-full rounded-full transition-all ${cpd.hours_this_year >= 20 ? 'bg-green-500' : 'bg-[#20acf6]'}`}
                  style={{ width: `${Math.min(100, (cpd.hours_this_year / 20) * 100)}%` }}
                />
              </div>
              {Object.keys(cpd.by_category).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(cpd.by_category).slice(0, 4).map(([cat, hours]) => (
                    <span key={cat} className="text-xs bg-[#F8FAFC] text-slate-600 rounded-full px-2.5 py-1 border border-slate-100">
                      {cat}: <strong className="text-[#2a3e5d]">{hours}h</strong>
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Continue Learning */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Continue Learning</CardTitle>
            <Button variant="ghost" asChild>
              <Link to="/my-courses">
                View all <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse space-y-4">
                {[1, 2].map((i) => (
                  <div key={i} className="h-24 bg-slate-100 rounded-lg" />
                ))}
              </div>
            ) : myCourses.length === 0 && enrollments.filter(e => e.status === 'active').length === 0 ? (
              <div className="text-center py-8">
                <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 mb-4">You haven't enrolled in any courses yet.</p>
                <Button asChild className="bg-[#20acf6] hover:bg-[#3bb8f7]">
                  <Link to="/courses">Browse Courses</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {(myCourses.length > 0 ? myCourses : enrollments.filter(e => e.status === 'active'))
                  .slice(0, 5)
                  .map((item) => {
                    const courseId = item.course_id || item.id;
                    const title = item.course_title || item.title;
                    const thumb = item.course_thumbnail || item.thumbnail_url;
                    const pct = item.progress?.overall_progress_pct || item.progress_percentage || 0;
                    const status = item.progress?.status || item.status || 'active';
                    return (
                    <div
                      key={courseId}
                      className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl"
                      data-testid={`my-course-${courseId}`}
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-200 flex-shrink-0">
                        <img
                          src={thumb || 'https://images.unsplash.com/photo-1762341122735-d6ae18da4c01?crop=entropy&cs=srgb&fm=jpg&w=200'}
                          alt={title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <h4 className="font-medium text-slate-900 truncate">{title}</h4>
                          {item.due_date && status !== 'completed' && (
                            <Badge className={new Date(`${item.due_date}T23:59:59`) < new Date() ? 'bg-red-100 text-red-700 shrink-0' : 'bg-amber-100 text-amber-700 shrink-0'} data-testid={`due-badge-${courseId}`}>
                              {new Date(`${item.due_date}T23:59:59`) < new Date() ? 'Overdue' : 'Due'} {new Date(`${item.due_date}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Progress value={pct} className="h-2 flex-1" />
                          <span className="text-sm text-slate-600 w-12 text-right">
                            {Math.round(pct)}%
                          </span>
                        </div>
                      </div>
                      <Button
                        asChild
                        size="sm"
                        className={status === 'completed' ? 'bg-green-600 hover:bg-green-700' : 'bg-[#20acf6] hover:bg-[#3bb8f7]'}
                      >
                        <Link to={`/learn/${courseId}`}>
                          <Play className="h-4 w-4 mr-1" />
                          {status === 'completed' ? 'Review' : 'Continue'}
                        </Link>
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Certificates */}
        {certificates.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold">Recent Certificates</CardTitle>
              <Button variant="ghost" asChild>
                <Link to="/certificates">
                  View all <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {certificates.slice(0, 3).map((cert) => (
                  <div
                    key={cert.id}
                    className="p-4 border border-slate-200 rounded-xl hover:shadow-md transition-shadow"
                  >
                    <Award className="h-8 w-8 text-[#20acf6] mb-3" />
                    <h4 className="font-medium text-slate-900 mb-1 line-clamp-1">
                      {cert.course_title}
                    </h4>
                    <p className="text-sm text-slate-500">
                      {new Date(cert.completion_date).toLocaleDateString()}
                    </p>
                    <Button
                      variant="link"
                      className="p-0 h-auto mt-2 text-blue-600"
                      asChild
                    >
                      <Link to={`/certificates/${cert.id}`}>
                        View Certificate
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Saved Courses (Wishlist) */}
        {wishlistCourses.length > 0 && (
          <Card className="border-slate-200" data-testid="wishlist-section">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Heart className="h-5 w-5 text-red-500" /> Saved Courses
              </CardTitle>
              <Button variant="ghost" asChild>
                <Link to="/courses">
                  Browse more <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {wishlistCourses.slice(0, 6).map((course) => (
                  <Link
                    key={course.id}
                    to={`/courses/${course.id}`}
                    className="block p-4 border border-slate-200 rounded-xl hover:shadow-md hover:border-[#20acf6]/30 transition-all group"
                    data-testid={`wishlist-course-${course.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                        {course.thumbnail_url ? (
                          <img src={course.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><BookOpen className="h-5 w-5 text-slate-300" /></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-[#2a3e5d] line-clamp-2 group-hover:text-[#20acf6] transition-colors">{course.title}</h4>
                        <p className="text-xs text-slate-500 mt-1">
                          {course.is_free || course.price <= 0 ? (
                            <span className="text-[#02d1d4] font-semibold">Free</span>
                          ) : (
                            <span>£{(course.price || 0).toFixed(2)}</span>
                          )}
                          {course.duration_hours > 0 && <span className="ml-2">{course.duration_hours}h</span>}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recommended Courses */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Recommended for You</CardTitle>
            <Button variant="ghost" asChild>
              <Link to="/courses">
                Browse all <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <p className="text-slate-600">
                Explore our course catalog to find training that matches your goals.
              </p>
              <Button asChild className="mt-4 bg-[#20acf6] hover:bg-[#3bb8f7]">
                <Link to="/courses">Browse Courses</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
    {showQuiz && <LearningStyleQuiz open={showQuiz} onClose={() => setShowQuiz(false)} />}
    </>
  );
};

export default LearnerDashboard;
